import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../api/axiosConfig";
import { motion } from "framer-motion";
import { Gavel, Clock, TrendingUp, Tag } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import MobileBottomBar from "../components/MobileBottomBar";

dayjs.extend(relativeTime);
dayjs.locale("es");

const BASE_URL = (process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") || "http://localhost:4000").replace(/\/$/, "");

function effectiveStatus(auction) {
  const now = new Date();
  if (auction.status === "active"    && new Date(auction.endAt)   <= now) return "ended";
  if (auction.status === "scheduled" && new Date(auction.startAt) <= now) return "active";
  return auction.status;
}

function Countdown({ endAt }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [urgent,   setUrgent]   = useState(false);

  useEffect(() => {
    const calc = () => {
      const diff = dayjs(endAt).diff(dayjs(), "second");
      if (diff <= 0) { setTimeLeft("Finalizada"); return; }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setUrgent(diff < 3600);
      setTimeLeft(h > 0 ? `${h}h ${m}m` : `${m}m ${String(s).padStart(2, "0")}s`);
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [endAt]);

  return (
    <span className={`font-mono font-bold text-xs ${urgent ? "text-red-500" : "text-gray-600"}`}>
      {timeLeft}
    </span>
  );
}

const STATUS = {
  active:    { label: "En vivo",    dot: "bg-green-500",  text: "text-green-700",  bg: "bg-green-50",  pulse: true },
  scheduled: { label: "Próxima",    dot: "bg-blue-500",   text: "text-blue-700",   bg: "bg-blue-50",   pulse: false },
  ended:     { label: "Finalizada", dot: "bg-gray-400",   text: "text-gray-500",   bg: "bg-gray-100",  pulse: false },
  sold:      { label: "Vendida",    dot: "bg-yellow-500", text: "text-yellow-700", bg: "bg-yellow-50", pulse: false },
  cancelled: { label: "Cancelada",  dot: "bg-red-400",    text: "text-red-600",    bg: "bg-red-50",    pulse: false },
};

function StatusBadge({ status }) {
  const s = STATUS[status] ?? STATUS.ended;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} ${s.pulse ? "animate-pulse" : ""}`} />
      {s.label}
    </span>
  );
}

export default function AuctionList() {
  const [auctions, setAuctions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("active");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter === "all" ? "draft,scheduled,active,ended,sold,cancelled" : filter;
      const res = await API.get(`/auctions?status=${statusParam}&limit=50`);
      const raw = res.data?.data?.auctions ?? [];
      const filtered = filter === "active"
        ? raw.filter((a) => effectiveStatus(a) === "active")
        : filter === "scheduled"
        ? raw.filter((a) => effectiveStatus(a) === "scheduled")
        : raw;
      setAuctions(filtered);
    } catch {
      setAuctions([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: "active",    label: "En vivo" },
    { key: "scheduled", label: "Próximas" },
    { key: "ended",     label: "Finalizadas" },
    { key: "all",       label: "Todas" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header compacto */}
      <div className="bg-zinc-900 px-4 pt-10 pb-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 bg-supernova-400 rounded-xl flex items-center justify-center">
              <Gavel size={18} className="text-black" />
            </div>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">Subastas</h1>
          </div>
          <p className="text-zinc-400 text-sm ml-12">Puja en tiempo real y consigue los mejores precios</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-5xl mx-auto flex gap-1 px-4 py-2 overflow-x-auto scrollbar-hide">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                filter === t.key
                  ? "bg-zinc-900 text-white"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl h-52 animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : auctions.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Gavel size={40} className="mx-auto mb-3 opacity-25" />
            <p className="text-sm font-semibold">No hay subastas en este estado</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {auctions.map((auction, i) => {
              const status = effectiveStatus(auction);
              const bidCount = auction._count?.bids ?? auction.totalBids ?? 0;
              return (
                <motion.div
                  key={auction.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={`/auction/${auction.id}`}
                    className="flex flex-col bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-md hover:border-gray-200 transition group"
                  >
                    {/* Imagen */}
                    <div className="relative h-36 bg-gray-100 overflow-hidden">
                      {auction.images?.[0] ? (
                        <img
                          src={auction.images[0].startsWith("http") ? auction.images[0] : `${BASE_URL}/${auction.images[0]}`}
                          alt={auction.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gavel size={32} className="text-gray-200" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <StatusBadge status={status} />
                      </div>
                      {status === "active" && (
                        <div className="absolute top-2 right-2 bg-black/55 backdrop-blur-sm rounded-lg px-1.5 py-0.5 flex items-center gap-1">
                          <Clock size={10} className="text-white/80" />
                          <Countdown endAt={auction.endAt} />
                        </div>
                      )}
                      {status === "scheduled" && (
                        <div className="absolute top-2 right-2 bg-black/55 backdrop-blur-sm rounded-lg px-1.5 py-0.5">
                          <span className="text-xs text-white font-medium">
                            {dayjs(auction.startAt).fromNow()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3 flex flex-col gap-1.5 flex-1">
                      {auction.company?.name && (
                        <p className="text-xs text-gray-400 truncate">{auction.company.name}</p>
                      )}
                      <h3 className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">
                        {auction.title}
                      </h3>

                      <div className="flex items-end justify-between mt-auto pt-1">
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Precio actual</p>
                          <p className="text-base font-extrabold text-gray-900">
                            ${Number(auction.currentPrice).toLocaleString("es-CO")}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-0.5">
                            <TrendingUp size={11} /> {bidCount}
                          </span>
                          {auction.buyNowPrice && (
                            <span className="flex items-center gap-0.5 text-supernova-600 font-semibold">
                              <Tag size={10} /> BIN
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <MobileBottomBar />
    </div>
  );
}
